import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Typography, TextField, Button, Grid, Paper, Divider,
  Switch, FormControlLabel, Alert, CircularProgress, Snackbar,
  Card, CardContent, Avatar, Chip, MenuItem,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RestoreIcon from "@mui/icons-material/Restore";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PaletteIcon from "@mui/icons-material/Palette";
import BrushIcon from "@mui/icons-material/Brush";
import LanguageIcon from "@mui/icons-material/Language";
import EmailIcon from "@mui/icons-material/Email";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import axios from "axios";
import { Link } from "react-router-dom";
import { ip_back } from "../../config";
import { getClubJoinPublicUrl } from "../../utils/clubJoinUrl";
import { useBrand } from "../../context/BrandContext";
import { SideBar } from "../../components/AdminLayout/SideBar";
import { TopBar } from "../../components/AdminLayout/TopBar";
import { canAccessClubBranding } from "../../utils/brandAccess";
import {
  extractPaletteFromUrl,
  extractPaletteFromFile,
} from "../../utils/extractPaletteFromImage";
import { LANG_PALETTES, brandDefaultsForLang } from "../../constants/brandDefaults";
import { clubLanguageFromStoredUser } from "../../utils/clubCountryLanguage";

/** Same chrome as AdminLayout: sidebar + top bar with club logo */
function BrandSettingsShell({ children, contentSx = {} }) {
  return (
    <Box sx={{ display: "flex", width: "100%" }}>
      <SideBar />
      <Box
        id="content"
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          minWidth: 0,
        }}
      >
        <TopBar />
        <Box sx={{ flex: 1, overflow: "auto", ...contentSx }}>{children}</Box>
      </Box>
    </Box>
  );
}

const COLOR_FIELDS = [
  { key: "color_primary", label: "Primary Color" },
  { key: "color_primary_dark", label: "Primary Dark" },
  { key: "color_primary_light", label: "Primary Light" },
  { key: "color_secondary", label: "Secondary Color" },
  { key: "color_accent", label: "Accent Color" },
  { key: "color_background", label: "Background" },
  { key: "color_surface", label: "Surface" },
  { key: "color_text_primary", label: "Text Primary" },
  { key: "color_text_secondary", label: "Text Secondary" },
];

function BrandSettings() {
  const { brand, refreshBrand } = useBrand();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const clubId = user?.club_id || user?.club_details?.club_id;
  const token = localStorage.getItem("token");

  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [extractingPalette, setExtractingPalette] = useState(false);
  const paletteFileInputRef = useRef(null);

  const fetchBrandConfig = useCallback(async () => {
    if (!clubId) return;
    try {
      const res = await axios.get(`${ip_back}api/brand/${clubId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.status === "success") {
        setFormData(res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch brand config:", err);
    } finally {
      setLoading(false);
    }
  }, [clubId, token]);

  useEffect(() => {
    fetchBrandConfig();
  }, [fetchBrandConfig]);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubdomainChange = (e) => {
    const v = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
    setFormData((prev) => ({ ...prev, club_subdomain: v }));
  };

  const handleToggle = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.checked }));
  };

  const handleSave = async () => {
    if (!clubId) return;
    setSaving(true);
    try {
      const res = await axios.put(`${ip_back}api/brand/${clubId}`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.status === "success") {
        setSnackbar({ open: true, message: "Brand settings saved successfully!", severity: "success" });
        await refreshBrand();
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to save brand settings";
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e, type = "main") => {
    const file = e.target.files?.[0];
    if (!file || !clubId) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("logo", file);
      const res = await axios.post(
        `${ip_back}api/brand/${clubId}/upload-logo?type=${type}`,
        fd,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );
      if (res.data.status === "success") {
        const updatedField = type === "light" ? "logo_light_url" : type === "favicon" ? "favicon_url" : "logo_url";
        setFormData((prev) => ({ ...prev, [updatedField]: res.data.data[updatedField] }));
        setSnackbar({ open: true, message: "Logo uploaded!", severity: "success" });
        await refreshBrand();
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Upload failed", severity: "error" });
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    fetchBrandConfig();
    setSnackbar({ open: true, message: "Changes reverted", severity: "info" });
  };

  const applyExtractedPalette = (palette) => {
    setFormData((prev) => ({ ...prev, ...palette }));
    setSnackbar({
      open: true,
      message: "Palette extracted — review colors and click Save Changes when ready.",
      severity: "success",
    });
  };

  const applyLangPalette = (lang) => {
    const defaults = brandDefaultsForLang(lang);
    setFormData((prev) => ({
      ...prev,
      color_primary: defaults.color_primary,
      color_primary_dark: defaults.color_primary_dark,
      color_primary_light: defaults.color_primary_light,
      color_secondary: defaults.color_secondary,
      color_accent: defaults.color_accent,
      default_language: lang,
    }));
    setSnackbar({
      open: true,
      message: `Applied ${LANG_PALETTES[lang]?.label || lang} country colors — save to persist.`,
      severity: "success",
    });
  };

  const applyCountryDefaultPalette = () => {
    const lang = clubLanguageFromStoredUser(user) || "en";
    applyLangPalette(lang);
  };

  /** Try main logo URL (needs CORS on the image host). */
  const handleExtractPaletteFromLogoUrl = async () => {
    const url = formData.logo_url?.trim();
    if (!url) {
      setSnackbar({
        open: true,
        message: "Set a main logo first, or use “Extract from image file”.",
        severity: "info",
      });
      paletteFileInputRef.current?.click();
      return;
    }
    setExtractingPalette(true);
    try {
      const palette = await extractPaletteFromUrl(url);
      applyExtractedPalette(palette);
    } catch {
      setSnackbar({
        open: true,
        message:
          "Could not read the logo URL (often CORS). Use “Extract from image file” with the same logo file.",
        severity: "warning",
      });
      paletteFileInputRef.current?.click();
    } finally {
      setExtractingPalette(false);
    }
  };

  const handleExtractPaletteFromFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setExtractingPalette(true);
    try {
      const palette = await extractPaletteFromFile(file);
      applyExtractedPalette(palette);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.message || "Could not extract colors from this image.",
        severity: "error",
      });
    } finally {
      setExtractingPalette(false);
    }
  };

  if (!canAccessClubBranding(user)) {
    return (
      <BrandSettingsShell contentSx={{ p: 3 }}>
        <Alert severity="error">
          Branding is only available to <strong>Super Administrators</strong>,{" "}
          <strong>club Administrators</strong>, and <strong>Moderators</strong>. Coaches
          cannot change club branding.
        </Alert>
        <Button component={Link} to="/Dashboard" variant="contained" sx={{ mt: 2 }}>
          Back to Dashboard
        </Button>
      </BrandSettingsShell>
    );
  }

  if (!clubId) {
    return (
      <BrandSettingsShell contentSx={{ p: 3 }}>
        <Alert severity="warning">No club associated with your account. Brand settings require a club.</Alert>
      </BrandSettingsShell>
    );
  }

  if (loading) {
    return (
      <BrandSettingsShell
        contentSx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </BrandSettingsShell>
    );
  }

  return (
    <BrandSettingsShell contentSx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: "auto" }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ fontFamily: "var(--brand-font)" }}>
              <PaletteIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              Brand Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Customize your club's branding, colors, and identity
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" startIcon={<RestoreIcon />} onClick={handleReset}>Reset</Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}
              sx={{ background: "var(--brand-primary)", "&:hover": { background: "var(--brand-primary-dark)" } }}>
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Identity Section */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                <BrushIcon sx={{ mr: 1, verticalAlign: "middle", color: "var(--brand-primary)" }} />
                Identity
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TextField label="Brand Name" fullWidth margin="normal" value={formData.brand_name || ""} onChange={handleChange("brand_name")} />
              <TextField label="Short Name" fullWidth margin="normal" value={formData.brand_short_name || ""} onChange={handleChange("brand_short_name")} />
              <TextField
                label="Tagline"
                fullWidth
                margin="normal"
                multiline
                minRows={2}
                value={formData.brand_tagline || ""}
                onChange={handleChange("brand_tagline")}
                helperText="Use Enter for a line break on the landing page."
              />
              <TextField label="Font Family" fullWidth margin="normal" value={formData.font_family || ""} onChange={handleChange("font_family")} helperText="e.g. Poppins, Roboto, Inter" />
            </Paper>
          </Grid>

          {/* Logo Upload Section */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                <CloudUploadIcon sx={{ mr: 1, verticalAlign: "middle", color: "var(--brand-primary)" }} />
                Logos & Icons
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {/* Main Logo */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Main Logo</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {formData.logo_url && (
                    <Avatar variant="rounded" src={formData.logo_url} sx={{ width: 80, height: 60 }} imgProps={{ style: { objectFit: "contain" } }} />
                  )}
                  <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} disabled={uploading}>
                    {uploading ? "Uploading..." : "Upload Logo"}
                    <input type="file" hidden accept="image/*" onChange={(e) => handleLogoUpload(e, "main")} />
                  </Button>
                </Box>
              </Box>

              {/* Light Logo */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Logo (Light / Dark BG)</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {formData.logo_light_url && (
                    <Avatar variant="rounded" src={formData.logo_light_url}
                      sx={{ width: 80, height: 60, bgcolor: "#333" }} imgProps={{ style: { objectFit: "contain" } }} />
                  )}
                  <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} disabled={uploading}>
                    Upload Light Logo
                    <input type="file" hidden accept="image/*" onChange={(e) => handleLogoUpload(e, "light")} />
                  </Button>
                </Box>
              </Box>

              {/* Favicon */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>Favicon</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {formData.favicon_url && (
                    <Avatar variant="rounded" src={formData.favicon_url} sx={{ width: 32, height: 32 }} />
                  )}
                  <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} disabled={uploading}>
                    Upload Favicon
                    <input type="file" hidden accept="image/*" onChange={(e) => handleLogoUpload(e, "favicon")} />
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Color Palette Section */}
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                <PaletteIcon sx={{ mr: 1, verticalAlign: "middle", color: "var(--brand-primary)" }} />
                Color Palette
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Button
                  variant="contained"
                  size="small"
                  startIcon={
                    extractingPalette ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <AutoAwesomeIcon />
                    )
                  }
                  disabled={extractingPalette}
                  onClick={handleExtractPaletteFromLogoUrl}
                  sx={{
                    background: "var(--brand-primary)",
                    "&:hover": { background: "var(--brand-primary-dark)" },
                  }}
                >
                  Extract from logo
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ImageSearchIcon />}
                  disabled={extractingPalette}
                  onClick={() => paletteFileInputRef.current?.click()}
                >
                  Extract from image file…
                </Button>
                <input
                  ref={paletteFileInputRef}
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleExtractPaletteFromFile}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Samples colors from your main logo (or any image). Remote logos must allow cross-origin access;
                if that fails, use the file option with your logo. Click <strong>Save Changes</strong> to persist.
              </Typography>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Country language defaults
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
                <Button size="small" variant="contained" onClick={applyCountryDefaultPalette}
                  sx={{ background: "var(--brand-primary)", "&:hover": { background: "var(--brand-primary-dark)" } }}>
                  Use my club country
                </Button>
                {Object.entries(LANG_PALETTES).map(([code, pal]) => (
                  <Button
                    key={code}
                    size="small"
                    variant="outlined"
                    onClick={() => applyLangPalette(code)}
                    startIcon={
                      <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: pal.color_primary }} />
                    }
                  >
                    {pal.label}
                  </Button>
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Clubs without a custom palette use these colors from the club country language (Flanders Dutch, Wallonia French, Luxembourg French, and so on).
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {COLOR_FIELDS.map((cf) => (
                  <Grid size={{ xs: 6, sm: 4, md: 3 }} key={cf.key}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <input
                        type="color"
                        value={formData[cf.key] || "#000000"}
                        onChange={(e) => setFormData((p) => ({ ...p, [cf.key]: e.target.value }))}
                        style={{ width: 48, height: 48, border: "2px solid #e0e0e0", borderRadius: 8, cursor: "pointer", padding: 2 }}
                      />
                      <Box>
                        <Typography variant="caption" fontWeight={600}>{cf.label}</Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {formData[cf.key] || "—"}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          {/* Live Preview */}
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                <VisibilityIcon sx={{ mr: 1, verticalAlign: "middle", color: "var(--brand-primary)" }} />
                Live Preview
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Card sx={{
                background: `linear-gradient(135deg, ${formData.color_primary || brand.color_primary} 0%, ${formData.color_primary_dark || brand.color_primary_dark} 100%)`,
                color: "#fff", borderRadius: 3, p: 3, mb: 2,
              }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    {formData.logo_url && (
                      <img src={formData.logo_url} alt="Preview" style={{ maxHeight: 40, objectFit: "contain" }} />
                    )}
                    <Typography variant="h5" fontWeight={700} sx={{ fontFamily: formData.font_family || "Poppins" }}>
                      {formData.brand_name || "Your Brand"}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      opacity: 0.9,
                      fontFamily: formData.font_family || "Poppins",
                      whiteSpace: "pre-line",
                    }}
                  >
                    {formData.brand_tagline || "Your tagline here"}
                  </Typography>
                </CardContent>
              </Card>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button variant="contained"
                  sx={{ bgcolor: formData.color_primary || brand.color_primary, "&:hover": { bgcolor: formData.color_primary_dark || brand.color_primary_dark }, fontFamily: formData.font_family || brand.font_family || "Poppins" }}>
                  Primary Button
                </Button>
                <Button variant="outlined"
                  sx={{ borderColor: formData.color_primary || brand.color_primary, color: formData.color_primary || brand.color_primary, fontFamily: formData.font_family || brand.font_family || "Poppins" }}>
                  Secondary Button
                </Button>
                <Chip label="Tag Sample" sx={{ bgcolor: formData.color_accent || "#FFEFDD", fontFamily: formData.font_family || "Poppins" }} />
                <Chip label="Active" sx={{ bgcolor: formData.color_primary || brand.color_primary, color: "#fff" }} />
              </Box>
            </Paper>
          </Grid>

          {/* Club URL & SEO */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                <LanguageIcon sx={{ mr: 1, verticalAlign: "middle", color: "var(--brand-primary)" }} />
                Club URL & SEO
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TextField
                label="Club URL name"
                fullWidth
                margin="normal"
                value={formData.club_subdomain || ""}
                onChange={handleSubdomainChange}
                placeholder="myclub"
                helperText={
                  formData.club_subdomain
                    ? `Join link: ${getClubJoinPublicUrl({
                        club_subdomain: formData.club_subdomain,
                        club_id: clubId,
                      })}`
                    : "Players join at https://smartttclub.com/join/your-club-name"
                }
              />
              <TextField label="SEO Meta Description" fullWidth margin="normal" multiline rows={2} value={formData.meta_description || ""} onChange={handleChange("meta_description")} />
              <TextField label="Default Language" fullWidth margin="normal" value={formData.default_language || "en"} onChange={handleChange("default_language")}
                helperText="en, fr, de, es, nl, pt — defaults from the club country language" />
            </Paper>
          </Grid>

          {/* Email & Features */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                <EmailIcon sx={{ mr: 1, verticalAlign: "middle", color: "var(--brand-primary)" }} />
                Email & Features
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TextField label="Email From Name" fullWidth margin="normal" value={formData.email_from_name || ""} onChange={handleChange("email_from_name")} />
              <TextField label="Email Footer Text" fullWidth margin="normal" multiline rows={2} value={formData.email_footer_text || ""} onChange={handleChange("email_footer_text")} />
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={<Switch checked={formData.show_ai_coach ?? true} onChange={handleToggle("show_ai_coach")} />}
                  label="Show AI Coach"
                />
                <FormControlLabel
                  control={<Switch checked={formData.show_powered_by ?? true} onChange={handleToggle("show_powered_by")} />}
                  label='Show "Powered by Smart TT"'
                />
                <TextField
                  select
                  label="Admin sidebar pack"
                  fullWidth
                  margin="normal"
                  value={formData.admin_feature_pack || "full"}
                  onChange={handleChange("admin_feature_pack")}
                  helperText="Pilot: Club, People, Groups, Competitions, Templates, Branding, Invite. Full: all admin tools."
                >
                  <MenuItem value="pilot">Pilot</MenuItem>
                  <MenuItem value="full">Full platform</MenuItem>
                </TextField>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>
    </BrandSettingsShell>
  );
}

export default BrandSettings;
